import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
    DYNAMIC_TABLE_ATTRIBUTE_LIST_BY_TABLE_ID_GET,
    PARTICULAR_REQUEST_PARAMETER_LIST_GET,
} from '../../../api/ApiConst';
import { TABLE_ATTRIBUTE_TYPES } from '../../../settings/constants/types';
import ApiCaller from '../../../api/ApiCaller';
import HeadersForm from './components/HeadersForm';
import RequestBodyForm from './components/RequestBodyForm';
import ResponseParametersForm from './components/ResponseParametersForm';
import UpdateConditionForm from './components/UpdateConditionForm';
import {
    InsertAction,
    UpdateAction,
    DeleteAction,
    CallApiAction,
    AggregationAction,
    SendEmailAction,
    ConditionAction,
    MergeAction,
    ResponseFormatAction
} from './actions';

// Constants
const OPERATORS = [
    { value: 'EQUAL_TO', label: 'EQUAL_TO' },
    { value: 'NOT_EQUAL_TO', label: 'NOT_EQUAL_TO' },
    { value: 'GREATER_THAN', label: 'GREATER_THAN' },
    { value: 'LESS_THAN', label: 'LESS_THAN' },
    { value: 'GREATER_THAN_EQUAL_TO', label: 'GREATER_THAN_EQUAL_TO' },
    { value: 'LESS_THAN_EQUAL_TO', label: 'LESS_THAN_EQUAL_TO' },
    { value: 'INCLUDES', label: 'INCLUDES' },
    { value: 'NOT_INCLUDES', label: 'NOT_INCLUDES' },
    { value: 'STARTS_WITH', label: 'STARTS_WITH' },
    { value: 'ENDS_WITH', label: 'ENDS_WITH' },
    { value: 'CONTAINS', label: 'CONTAINS' },
    { value: 'NOT_CONTAINS', label: 'NOT_CONTAINS' }
];

const ActionConfigModal = ({ 
    show, 
    action, 
    onSave, 
    onClose, 
    allTableList, 
    allSteps, 
    selectedRequest, 
    currentStepId 
}) => {
    // State Management
    const [formData, setFormData] = useState({});
    const [allPreviousSteps, setAllPreviousSteps] = useState([]);
    const [allNextSteps, setAllNextSteps] = useState([]);
    const [conditionalSteps, setConditionalSteps] = useState([{ condition: '', stepId: '' }]);
    const [elseStepId, setElseStepId] = useState('');

    // Effects
    useEffect(() => {
        if (action) {
            setFormData(action.config || {});
        }
    }, [action]);

    useEffect(() => {
        if (allSteps && currentStepId) {
            const currentStepIndex = allSteps.findIndex(step => step.id === currentStepId);
            if (currentStepIndex !== -1) {
                setAllPreviousSteps(allSteps.slice(0, currentStepIndex));
                setAllNextSteps(allSteps.slice(currentStepIndex + 1));
            }
        }
    }, [allSteps, currentStepId]);

    // Event Handlers
    const handleSubmit = () => {
        if (!action) return;

        let isValid = true;
        let errorMessage = '';

        switch (action.type) {
            case 'INSERT':
            case 'UPDATE':
                if (!formData.tableId) {
                    isValid = false;
                    errorMessage = 'Please select a table';
                } else if (!formData.mappings || Object.keys(formData.mappings).length === 0) {
                    isValid = false;
                    errorMessage = 'Please configure at least one mapping';
                }
                break;
            case 'DELETE':
                console.log('DELETE validation - formData:', formData);
                console.log('DELETE validation - condition:', formData.condition);
                if (!formData.tableId) {
                    isValid = false;
                    errorMessage = 'Please select a table';
                } else if (!formData.condition || !formData.condition.operation) {
                    isValid = false;
                    errorMessage = 'Please configure delete condition (at least operator required)';
                } else {
                    // For logical operators (AND, OR), we don't need columnName or columnValue
                    const isLogicalOperator = ['AND', 'OR'].includes(formData.condition.operation);
                    if (!isLogicalOperator && (!formData.condition.columnName || !formData.condition.columnValue)) {
                        isValid = false;
                        errorMessage = 'Please configure delete condition (field and value required for non-logical operators)';
                    }
                }
                break;
            case 'CALL_API':
                if (!formData.method || !formData.endpoint) {
                    isValid = false;
                    errorMessage = 'Please configure method and endpoint';
                }
                break;
            case 'AGGREGATION':
                if (!formData.type) {
                    isValid = false;
                    errorMessage = 'Please select a type';
                } else if (!formData.tableId) {
                    isValid = false;
                    errorMessage = 'Please select a collection';
                } else if (!formData.aggregationQuery || formData.aggregationQuery.trim() === '') {
                    isValid = false;
                    errorMessage = 'Please enter an aggregation query';
                }
                break;
            case 'MERGE':
                if (!Array.isArray(formData.sourceObjects) || formData.sourceObjects.length < 2) {
                    isValid = false;
                    errorMessage = 'Please select at least two objects to merge';
                } else if (!formData.resultAs || !formData.resultAs.trim()) {
                    isValid = false;
                    errorMessage = 'Please provide a result variable name';
                }
                break;
            case 'RESPONSE_FORMAT':
                if (!formData.responseMappings || formData.responseMappings.length === 0) {
                    isValid = false;
                    errorMessage = 'Please configure at least one response mapping';
                } else {
                    const hasInvalidMapping = formData.responseMappings.some(mapping => 
                        !mapping.responseField || !mapping.sourceStep || !mapping.sourceParameter
                    );
                    if (hasInvalidMapping) {
                        isValid = false;
                        errorMessage = 'Please complete all response mapping configurations';
                    }
                }
                break;
            case 'SEND_EMAIL':
                if (!formData.emailTemplate || !formData.toEmail || !formData.subject) {
                    isValid = false;
                    errorMessage = 'Please configure email template, recipient email, and subject';
                }
                break;
            case 'CONDITION':
                if (!formData.conditionalSteps || formData.conditionalSteps.length === 0) {
                    isValid = false;
                    errorMessage = 'Please configure at least one conditional step';
                } else {
                    const hasInvalidCondition = formData.conditionalSteps.some(step => 
                        !step.conditionField || !step.conditionOperator || !step.conditionValue || !step.stepId
                    );
                    if (hasInvalidCondition) {
                        isValid = false;
                        errorMessage = 'Please complete all conditional step configurations';
                    }
                }
                break;
            default:
                break;
        }

        if (!isValid) {
            toast.error(errorMessage);
            return;
        }

        // Sanitize responseParameters structure before saving
        const cleanAttributesRecursively = (attrs = []) => {
            if (!Array.isArray(attrs)) return [];
            return attrs.map((attr) => {
                if (!attr) return attr;
                const cleaned = { ...attr };
                // Always remove direct attributes array at this level
                const nested = attr.config?.attributes || attr.attributes || [];
                if ((attr.type || '').toUpperCase() === TABLE_ATTRIBUTE_TYPES.OBJECT) {
                    cleaned.config = { ...(cleaned.config || {}), attributes: cleanAttributesRecursively(nested) };
                }
                delete cleaned.attributes;
                return cleaned;
            });
        };

        const cleanedConfig = { ...formData };
        if (Array.isArray(formData?.responseParameters)) {
            cleanedConfig.responseParameters = formData.responseParameters.map((param) => {
                const cleanedParam = { ...param };
                const isObjectType = (cleanedParam.type || '').toUpperCase() === TABLE_ATTRIBUTE_TYPES.OBJECT;
                if (isObjectType) {
                    const nested = cleanedParam.config?.attributes || cleanedParam.attributes || [];
                    cleanedParam.config = { ...(cleanedParam.config || {}), attributes: cleanAttributesRecursively(nested) };
                }
                delete cleanedParam.attributes; // never keep direct attributes on param
                return cleanedParam;
            });
        }

        onSave({
            ...action,
            config: cleanedConfig
        });
    };

    const renderFields = () => {
        if (!action) return null;

        const commonProps = {
            formData,
            setFormData,
            allTableList,
            selectedRequest,
            allPreviousSteps,
            currentStepId
        };

        switch (action.type) {
            case 'INSERT':
                return <InsertAction {...commonProps} />;
            case 'UPDATE':
                return <UpdateAction {...commonProps} />;
            case 'DELETE':
                return <DeleteAction {...commonProps} />;
            case 'CALL_API':
                return <CallApiAction {...commonProps} />;
            case 'AGGREGATION':
                return <AggregationAction {...commonProps} />;
            case 'SEND_EMAIL':
                return <SendEmailAction {...commonProps} />;
            case 'CONDITION':
                return <ConditionAction {...commonProps} allSteps={allSteps} />;
            case 'MERGE':
                return <MergeAction {...commonProps} />;
            case 'RESPONSE_FORMAT':
                return <ResponseFormatAction {...commonProps} />;
            default:
                return <div>Unknown action type: {action.type}</div>;
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Configure {action?.type || 'Action'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {renderFields()}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Function to render action configuration details for display
export const renderActionConfigDetails = (action, allTableList) => {
    if (!action) return <div>No action configured</div>;

    const config = action.config || {};
    const table = allTableList?.find(t => t.id === config.tableId);

    const renderMappingList = (mappings) => {
        if (!mappings || Object.keys(mappings).length === 0) {
            return <div className="text-muted">No mappings configured</div>;
        }

        return (
            <ul className="list-unstyled">
                {Object.entries(mappings).map(([key, value]) => (
                    <li key={key} className="mb-1">
                        <strong>{key}:</strong> {value}
                    </li>
                ))}
            </ul>
        );
    };

    const renderResponseParameters = (responseParameters) => {
        if (!responseParameters || responseParameters.length === 0) {
            return <div className="text-muted">No response parameters configured</div>;
        }

        return (
            <ul className="list-unstyled">
                {responseParameters.map((param, index) => (
                    <li key={index} className="mb-1">
                        <strong>{param.name}:</strong> {param.type}
                    </li>
                ))}
            </ul>
        );
    };

    const renderConditionalSteps = (conditionalSteps) => {
        if (!conditionalSteps || conditionalSteps.length === 0) {
            return <div className="text-muted">No conditional steps configured</div>;
        }

        return (
            <ul className="list-unstyled">
                {conditionalSteps.map((step, index) => (
                    <li key={index} className="mb-1">
                        <strong>Condition {index + 1}:</strong> {step.condition} → Step {step.stepId}
                    </li>
                ))}
            </ul>
        );
    };

    const parameterSourceLabel = (source) => {
        if (source === 'custom_request') return 'Custom Request';
        const stepMatch = source.match(/step_(\d+)/);
        return stepMatch ? `Step ${stepMatch[1]}` : source;
    };

    switch (action.type) {
        case 'INSERT':
        case 'UPDATE':
            return (
                <div>
                    <h6>Table Configuration</h6>
                    <p><strong>Table:</strong> {table?.name || 'Not selected'}</p>
                    
                    <h6>Mappings</h6>
                    {renderMappingList(config.mappings)}
                    
                    <h6>Response Parameters</h6>
                    {renderResponseParameters(config.responseParameters)}
                </div>
            );

        case 'DELETE':
            return (
                <div>
                    <h6>Table Configuration</h6>
                    <p><strong>Table:</strong> {table?.name || 'Not selected'}</p>
                    
                    <h6>Delete Condition</h6>
                    {config.condition ? (
                        <div>
                            <p><strong>Primary:</strong> {config.condition.columnName} {config.condition.operation} {config.condition.columnValue}</p>
                            {config.condition.subFilter && config.condition.subFilter.length > 0 && (
                                <div className="ms-3">
                                    {config.condition.subFilter.map((subCond, index) => (
                                        <p key={index}><strong>AND:</strong> {subCond.columnName} {subCond.operation} {subCond.columnValue}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-muted">No condition configured</p>
                    )}
                    
                    <h6>Response Parameters</h6>
                    {renderResponseParameters(config.responseParameters)}
                </div>
            );

        case 'CALL_API':
            return (
                <div>
                    <h6>API Configuration</h6>
                    <p><strong>Method:</strong> {config.method || 'Not configured'}</p>
                    <p><strong>Endpoint:</strong> {config.endpoint || 'Not configured'}</p>
                    
                    <h6>Headers</h6>
                    {config.headers && config.headers.length > 0 ? (
                        <ul className="list-unstyled">
                            {config.headers.map((header, index) => (
                                <li key={index} className="mb-1">
                                    <strong>{header.name}:</strong> {header.value}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-muted">No headers configured</div>
                    )}
                    
                    <h6>Response Parameters</h6>
                    {renderResponseParameters(config.responseParameters)}
                </div>
            );

        case 'AGGREGATION':
            return (
                <div>
                    <h6>Aggregation Configuration</h6>
                    <p><strong>Type:</strong> {config.type || 'Not configured'}</p>
                    <p><strong>Collection:</strong> {table?.name || 'Not selected'}</p>
                    
                    <h6>Aggregation Query</h6>
                    {config.aggregationQuery ? (
                        <div className="bg-light p-2 rounded">
                            <pre className="mb-0" style={{ fontSize: '12px' }}>
                                <code>{config.aggregationQuery}</code>
                            </pre>
                        </div>
                    ) : (
                        <div className="text-muted">No aggregation query configured</div>
                    )}
                    
                    <h6>Input Mappings</h6>
                    {config.mappings && Object.keys(config.mappings).length > 0 ? (
                        <ul className="list-unstyled">
                            {Object.entries(config.mappings).map(([key, value]) => (
                                <li key={key} className="mb-1">
                                    <strong>{key}:</strong> {value}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-muted">No input mappings configured</div>
                    )}
                    
                    <h6>Response Parameters</h6>
                    {renderResponseParameters(config.responseParameters)}
                </div>
            );

        case 'SEND_EMAIL':
            return (
                <div>
                    <h6>Email Configuration</h6>
                    <p><strong>To:</strong> {config.toEmail || 'Not configured'}</p>
                    <p><strong>Subject:</strong> {config.subject || 'Not configured'}</p>
                    
                    <h6>Email Template</h6>
                    <p>{config.emailTemplate || 'No template configured'}</p>
                    
                    <h6>Response Parameters</h6>
                    {renderResponseParameters(config.responseParameters)}
                </div>
            );

        case 'CONDITION':
            return (
                <div>
                    <h6>Conditional Steps</h6>
                    {renderConditionalSteps(config.conditionalSteps)}
                    
                    {config.elseStepId && (
                        <div>
                            <h6>Else Step</h6>
                            <p>Step {config.elseStepId}</p>
                        </div>
                    )}
                </div>
            );

        case 'MERGE':
            return (
                <div>
                    <h6>Merge Configuration</h6>
                    <p><strong>Sources:</strong> {(config.sourceObjects || []).join(', ') || 'Not selected'}</p>
                    <p><strong>Strategy:</strong> {config.mergeStrategy || 'Not set'}</p>
                    <p><strong>Conflict Resolution:</strong> {config.conflictResolution || 'Not set'}</p>
                    {config.includeKeys && (
                        <p><strong>Include Keys:</strong> {config.includeKeys}</p>
                    )}
                    {config.excludeKeys && (
                        <p><strong>Exclude Keys:</strong> {config.excludeKeys}</p>
                    )}
                    <p><strong>Result As:</strong> {config.resultAs || 'Not set'}</p>
                </div>
            );

        case 'RESPONSE_FORMAT':
            return (
                <div>
                    <h6>Response Format Configuration</h6>
                    <p><strong>Response Mappings:</strong> {config.responseMappings?.length || 0} mappings configured</p>
                    
                    <h6>Response Mappings</h6>
                    {config.responseMappings && config.responseMappings.length > 0 ? (
                        <ul className="list-unstyled">
                            {config.responseMappings.map((mapping, index) => (
                                <li key={index} className="mb-1">
                                    <strong>{mapping.responseField}:</strong> Step {mapping.sourceStep}.{mapping.sourceParameter}
                                    {mapping.sourceAttribute && ` → ${mapping.sourceAttribute}`}
                                    {mapping.transformation !== 'direct' && ` (${mapping.transformation})`}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="text-muted">No response mappings configured</div>
                    )}
                    
                    <h6>Response Parameters</h6>
                    {renderResponseParameters(config.responseParameters)}
                </div>
            );

        default:
            return <div>Unknown action type: {action.type}</div>;
    }
};

export default ActionConfigModal;



